import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import get from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'

import { EMPTY_LIST } from '../../../constants/common'
import {
  useInlineNotifications,
  useUk,
  useUkTimeZone,
  useUserData,
} from '../../../hooks'
import { isAdminUser, isStaffUser } from '../../../utils/user'
import { FormikForm } from '../../Formik'
import { isPostTypeTicker } from '../../Post'

import { FormFields } from './FormFields'
import { createPost, createTvTicker } from './NewsboardPostForm.api'
import { NEWSBOARD_POST_FORM_FIELD_NAME_TYPE } from './NewsboardPostForm.constants'
import {
  getNewsboardPostFormInitialValues,
  getNewsboardPostFormPayload,
  getNewsboardPostFormValidationSchema,
  parseNewsboardPostFormServerErrors,
} from './NewsboardPostForm.utils'

import * as styles from './NewsboardPostForm.module.css'

function NewsboardPostForm({
  FormContainer,
  containerProps,
  getInitialValues = getNewsboardPostFormInitialValues,
  getResetInitialValues = getNewsboardPostFormInitialValues,
  onCancel,
  onSuccess,
  ...restProps
}) {
  const { timezone } = useUkTimeZone()

  const { t } = useTranslation()

  const [files, setFiles] = useState(EMPTY_LIST)
  const [storageFiles, setStorageFiles] = useState(EMPTY_LIST)

  const user = useUserData()
  const isStaffUserAcc = isStaffUser(user)
  const isAdminUserAcc = isAdminUser(user)

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const { config: ukConfig } = useUk()

  const initialValues = getInitialValues(ukConfig, t)

  const resetInitialValues = getResetInitialValues(ukConfig, t)

  const validationSchema = getNewsboardPostFormValidationSchema(
    isStaffUserAcc,
    t
  )

  function handleSubmit(values, { setErrors, setSubmitting }) {
    const payload = getNewsboardPostFormPayload({
      files,
      isAdminUserAcc,
      isStaffUserAcc,
      storageFiles,
      timezone,
      values,
    })

    const api = isPostTypeTicker(
      values[NEWSBOARD_POST_FORM_FIELD_NAME_TYPE].value
    )
      ? createTvTicker
      : createPost

    api(payload)
      .then(({ data }) => {
        onSuccess?.(data)
      })
      .catch(error => {
        const errors = get(error, ['response', 'data'], {})

        if (!isEmpty(errors)) {
          setSubmitting(false)
          setErrors(parseNewsboardPostFormServerErrors(errors))
        }
      })
  }

  return (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={{
        ...containerProps,
        files,
      }}
      formProps={{
        className: styles.formBody,
      }}
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormFields
        {...restProps}
        files={files}
        isAdminUserAcc={isAdminUserAcc}
        isStaffUserAcc={isStaffUserAcc}
        notifications={notifications}
        resetInitialValues={resetInitialValues}
        setFiles={setFiles}
        setStorageFiles={setStorageFiles}
        storageFiles={storageFiles}
        onCancel={onCancel}
        onRemoveNotification={handleRemoveNotification}
        onSetNotification={handleAddNotification}
      />
    </FormikForm>
  )
}

export default NewsboardPostForm
